import React from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Layout } from "../components";
import { useAxiosInterceptor } from "../hooks";
import AddAccount from "../pages/Admin/accounts/AddAccount";
import AdminAccounts from "../pages/Admin/accounts/AdminAccounts";
import DetailsAccount from "../pages/Admin/accounts/DetailsAccount";
import Admin from "../pages/Admin/Admin";
import AdminAudit from "../pages/Admin/Audit";
import EntityPermissions from "../pages/Admin/entityPermissions/EntityPermissions";
import ReferenceTablePage from "../pages/Admin/reference-tables/ReferenceTablePage";
import SchemaTablesPage from "../pages/Admin/reference-tables/SchemaTablesPage";
import Variables from "../pages/Admin/reference-tables/Variables";
import AddUser from "../pages/Admin/users/AddUser";
import AdminUsers from "../pages/Admin/users/AdminUsers";
import ManageUser from "../pages/Admin/users/ManageUser";
import UserProfile from "../pages/Admin/users/UserProfile";
import AddCapture from "../pages/Capture/AddCapture";
import DetailsCapture from "../pages/Capture/DetailsCapture";
import ListCaptures from "../pages/Capture/ListCaptures";
import AddEntity from "../pages/Entity/AddEntity";
import DetailsEntity from "../pages/Entity/DetailsEntity";
import ListEntities from "../pages/Entity/ListEntities";
import ListExports from "../pages/Export/ListExports";
import AddFlowTest from "../pages/FlowTest/AddFlowTest";
import DetailsFlowTest from "../pages/FlowTest/DetailsFlowTest";
import Homepage from "../pages/Homepage";
import LandingPage from "../pages/LandingPage";
import Login from "../pages/Login";
import DetailsManagementUnit from "../pages/ManagementUnit/DetailsManagementUnit";
import WarehouseEquipmentPage from "../components/Warehouse/Tabs/EquipmentTab";
import { SessionStore } from "../types/store/SessionStore";
import { StoreInterfaces } from "../types/store/StoreInterfaces";
import AdminExports from "../pages/Export/AdminExports";
import DetailsWarehouse from "../pages/Warehouse/DetailsWarehouse";

const PrivateRoutes = () => {
  const accessToken: SessionStore["accessToken"] = useSelector(
    (state: StoreInterfaces) => state.session.accessToken,
  );
  return accessToken ? (
    <Layout>
      <Outlet />
    </Layout>
  ) : (
    <Navigate to="/login" />
  );
};

const PublicRoutes = () => {
  return <Outlet />;
};

const AppInterceptorWrapper = ({ children }: { children: React.ReactNode }) => {
  useAxiosInterceptor();
  return <>{children}</>;
};

const Router = () => {
  return (
    <AppInterceptorWrapper>
      <Routes>
        <Route element={<PrivateRoutes />}>
          <Route path="/home" element={<Homepage />} />
          <Route path="/admin" element={<Admin />}>
            <Route path="accounts" element={<AdminAccounts />} />
            <Route path="accounts/add-account" element={<AddAccount />} />
            <Route path="accounts/:accountId" element={<DetailsAccount />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/add-user" element={<AddUser />} />
            <Route path="users/:accountId/:userId" element={<ManageUser />} />
            <Route path="entity-permissions" element={<EntityPermissions />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="reference/:schema" element={<SchemaTablesPage />} />
            <Route
              path="reference/:schema/:table"
              element={<ReferenceTablePage />}
            />
            <Route path="reference/variables" element={<Variables />} />
            <Route path="exports" element={<AdminExports />} />
          </Route>
          <Route path="/entities">
            <Route path="entity-list" element={<ListEntities />} />
            <Route path="add-entity" element={<AddEntity />} />
            <Route path=":entityId" element={<DetailsEntity />} />
          </Route>
          <Route path="/captures">
            <Route path="capture-list" element={<ListCaptures />} />
            <Route path=":captureId" element={<DetailsCapture />} />
            <Route path="add-capture" element={<AddCapture />} />
            <Route
              path=":captureId/flow-tests/add-flow-test"
              element={<AddFlowTest />}
            />
          </Route>
          <Route path="/management-units">
            <Route
              path=":managementUnitId"
              element={<DetailsManagementUnit />}
            />
          </Route>
          <Route path="/flow-tests">
            <Route path=":flowTestId" element={<DetailsFlowTest />} />
          </Route>
          <Route path="/profile" element={<UserProfile />} />
          <Route
            path="/warehouse"
            element={<DetailsWarehouse />}
          />
          <Route path="/exports" element={<ListExports />} />
        </Route>

        <Route element={<PublicRoutes />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
        </Route>
      </Routes>
    </AppInterceptorWrapper>
  );
};

export default Router;
